<script>
  import ReceiptPageLayout from '../components/receipt/ReceiptPageLayout.svelte';
  import ReceiptCard from '../components/receipt/ReceiptCard.svelte';
  import ReceiptHeader from '../components/receipt/ReceiptHeader.svelte';
  import ReceiptLabel from '../components/receipt/ReceiptLabel.svelte';
  import ReceiptDivider from '../components/receipt/ReceiptDivider.svelte';
  import ReceiptItemRow from '../components/receipt/ReceiptItemRow.svelte';
  import ReceiptSummary from '../components/receipt/ReceiptSummary.svelte';
  import ReceiptFooter from '../components/receipt/ReceiptFooter.svelte';
  import ReceiptInput from '../components/receipt/ReceiptInput.svelte';

  const CURRENCY_SYMBOLS = { JPY: '¥', KRW: '₩', USD: '$', EUR: '€' };
  const CATEGORIES = ['all', 'food', 'transport', 'accommodation', 'shopping', 'activity', 'other'];

  let {
    trip = {
      id: 'trip-001',
      name: '도쿄 벚꽃 여행',
      destination: 'TOKYO',
      startDate: '2026-03-20',
      endDate: '2026-03-26',
      spentCents: 4500000,
      budgetCents: 6000000,
    },
    expenses = [
      { id: '1', merchantName: '이치란 라멘 신주쿠점', originalAmountCents: 189000, originalCurrency: 'JPY', krwAmountCents: 1890000, category: 'food', transactedAt: '2026-03-21', entryMethod: 'ocr', excluded: false },
      { id: '2', merchantName: '스이카 교통카드 충전', originalAmountCents: 300000, originalCurrency: 'JPY', krwAmountCents: 3000000, category: 'transport', transactedAt: '2026-03-21', entryMethod: 'manual', excluded: false },
      { id: '3', merchantName: '시부야 호텔 2박', originalAmountCents: 2500000, originalCurrency: 'JPY', krwAmountCents: 25000000, category: 'accommodation', transactedAt: '2026-03-20', entryMethod: 'manual', excluded: false },
      { id: '4', merchantName: '돈키호테 간식 쇼핑', originalAmountCents: 450000, originalCurrency: 'JPY', krwAmountCents: 4500000, category: 'shopping', transactedAt: '2026-03-22', entryMethod: 'ocr', excluded: false },
      { id: '5', merchantName: '팀랩 입장권', originalAmountCents: 320000, originalCurrency: 'JPY', krwAmountCents: 3200000, category: 'activity', transactedAt: '2026-03-23', entryMethod: 'manual', excluded: true },
      { id: '6', merchantName: '세븐일레븐 음료', originalAmountCents: 25000, originalCurrency: 'JPY', krwAmountCents: 250000, category: 'food', transactedAt: '2026-03-23', entryMethod: 'manual', excluded: false },
    ],
    companions = 4,
  } = $props();

  let selectedCategory = $state('all');
  let filterOpen = $state(false);

  let filteredExpenses = $derived(
    selectedCategory === 'all'
      ? expenses
      : expenses.filter(e => e.category === selectedCategory)
  );

  let subtotal = $derived(
    filteredExpenses
      .filter(e => !e.excluded)
      .reduce((sum, e) => sum + e.krwAmountCents, 0)
  );

  let excludedTotal = $derived(
    filteredExpenses
      .filter(e => e.excluded)
      .reduce((sum, e) => sum + e.krwAmountCents, 0)
  );

  let grandTotal = $derived(subtotal);

  function formatCents(cents) {
    return Math.floor(cents / 100).toLocaleString();
  }

  function getCurrencySymbol(code) {
    return CURRENCY_SYMBOLS[code] || code;
  }

  function formatTripDates(start, end) {
    const s = new Date(start);
    const e = new Date(end);
    const fmt = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return `${fmt(s)} - ${fmt(e)}`;
  }

  function getCategoryLabel(cat) {
    const labels = {
      all: 'ALL',
      food: 'FOOD',
      transport: 'TRANSPORT',
      accommodation: 'ACCOM',
      shopping: 'SHOPPING',
      activity: 'ACTIVITY',
      other: 'OTHER',
    };
    return labels[cat] || cat.toUpperCase();
  }

  function getCategoryCount(cat) {
    if (cat === 'all') return expenses.length;
    return expenses.filter(e => e.category === cat).length;
  }
</script>

<ReceiptPageLayout>
  {#snippet header()}
    <ReceiptCard variant="full">
      <ReceiptHeader
        title={trip.destination}
        subtitle={formatTripDates(trip.startDate, trip.endDate)}
        receiptNo="TH-{trip.id}"
        date="2026.03.17"
      />
    </ReceiptCard>
  {/snippet}

  {#snippet content()}
    <ReceiptCard variant="full">
      <div class="items-header">
        <ReceiptLabel text="ITEMS ({filteredExpenses.length})" />
        <button class="filter-btn" type="button" onclick={() => filterOpen = !filterOpen}>
          {getCategoryLabel(selectedCategory)} ▾
        </button>
      </div>

      {#if filterOpen}
        <div class="filter-dropdown">
          {#each CATEGORIES as cat}
            {@const count = getCategoryCount(cat)}
            {#if count > 0 || cat === 'all'}
              <button
                class="filter-option"
                class:filter-option--active={selectedCategory === cat}
                onclick={() => { selectedCategory = cat; filterOpen = false; }}
                type="button"
              >
                {getCategoryLabel(cat)} ({count})
              </button>
            {/if}
          {/each}
        </div>
      {/if}

      <ReceiptDivider variant="dashed" spacing="md" />

      {#each filteredExpenses as expense (expense.id)}
        <ReceiptItemRow
          name={expense.merchantName}
          amount={Math.floor(expense.originalAmountCents / 100)}
          currency={getCurrencySymbol(expense.originalCurrency)}
          quantity={1}
          category={expense.category}
          excluded={expense.excluded}
          note={expense.excluded ? '제외됨' : ''}
        />
      {/each}

      <ReceiptDivider variant="double" spacing="md" />

      <ReceiptSummary
        currency="₩"
        items={[
          { label: 'SUBTOTAL', amount: Math.floor(subtotal / 100) },
          { label: 'EXCLUDED', amount: Math.floor(excludedTotal / 100), excluded: true },
        ]}
        total={{ label: 'TOTAL', amount: Math.floor(grandTotal / 100) }}
      />

      <ReceiptFooter
        lines={[
          `TRIP: ${trip.name}`,
          `COMPANIONS: ${companions}명`,
        ]}
        timestamp="2026.03.17 14:23:05"
        showBarcode={true}
      />
    </ReceiptCard>
  {/snippet}

  {#snippet input()}
    <ReceiptInput placeholder="로손 편의점 1500엔..." />
  {/snippet}
</ReceiptPageLayout>

<style>
  .items-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .filter-btn {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: var(--receipt-text-secondary);
    background: transparent;
    border: 1px solid var(--receipt-divider);
    border-radius: 2px;
    padding: 3px 8px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .filter-btn:hover {
    color: var(--receipt-text-accent, #20B2AA);
    border-color: var(--receipt-text-accent, #20B2AA);
  }

  .filter-dropdown {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 8px;
    margin: 4px 0;
    background: var(--receipt-surface-hover, rgba(0,0,0,0.02));
    border: 1px solid var(--receipt-divider);
    border-radius: 2px;
  }

  .filter-option {
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: var(--receipt-text-muted);
    background: transparent;
    border: 1px solid var(--receipt-divider);
    border-radius: 2px;
    padding: 3px 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .filter-option:hover {
    color: var(--receipt-text-accent, #20B2AA);
    border-color: var(--receipt-text-accent, #20B2AA);
  }

  .filter-option--active {
    color: var(--receipt-text-accent, #20B2AA);
    border-color: var(--receipt-text-accent, #20B2AA);
    background: rgba(32, 178, 170, 0.08);
  }
</style>
